import { useState } from 'react'
import { useUI } from '../store/ui'
import { AccountsBody, SettingsBody } from './TopPanel'
import { ActivityFeed } from './ActivityFeed'
import { PinnedFeeds } from './PinnedFeeds'
import { GalleryControlsBody, ProfileGalleryControlsBody } from './GalleryControls'
import { FeedViewControlsBody } from './FeedViewControls'

const TITLE: Record<string, string> = {
  accounts: 'accounts',
  settings: 'settings',
  activity: 'activity',
  pinned:   'pinned',
  ai:       'AI',
}

const VIEW_TITLE: Record<string, string> = {
  feed:      'feeds',
  activity:  'gallery',
  discovery: 'discover',
  profile:   'profile',
}

/**
 * Collapsible right panel — 296px open, 18px strip when closed.
 * Opens on hover or pinned. Helm/gear/tool tabs swap its contents.
 */
export function RightPanel() {
  const panel    = useUI((s) => s.panel)
  const setPanel = useUI((s) => s.setPanel)
  const homeTab  = useUI((s) => s.homeTab)

  const [pinned, setPinned] = useState(true)
  const [hover,  setHover]  = useState(false)

  const hasBody = panel !== 'content'
  const open    = hasBody || pinned || hover
  const title   = hasBody ? (TITLE[panel] ?? 'panel') : (VIEW_TITLE[homeTab] ?? 'panel')

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative z-20 flex shrink-0 flex-col"
      style={{
        background: 'linear-gradient(180deg, #102341, #0a1830 45%, #081323)',
        width: open ? 296 : 18,
        transition: 'width .15s ease',
        boxShadow: '-16px 0 36px rgba(0,0,0,0.45)',
      }}
    >
      {!open ? (
        <button
          type="button"
          onClick={() => setPinned(true)}
          title="expand panel"
          className="flex h-full w-full flex-col items-center gap-2 pt-2 hover:brightness-125"
          style={{ background: '#102341', boxShadow: 'inset 1px 0 0 #8aa0c8' }}
        >
          <span className="text-sm font-bold leading-none text-ink-100">‹</span>
          <span
            className="text-[8px] font-bold uppercase tracking-[0.08em] text-ink-300"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {title}
          </span>
        </button>
      ) : (
        <div className="flex h-full flex-col" style={{ width: 296 }}>
          {/* header */}
          <div className="flex h-[34px] shrink-0 items-center" style={{ background: '#102341' }}>
            <span className="px-3 text-[11px] font-bold uppercase tracking-wide text-white">
              {title}
            </span>
            <button
              type="button"
              onClick={() => setPinned((p) => !p)}
              title={pinned ? 'unpin' : 'keep open'}
              className="ml-auto grid h-full place-items-center px-2"
            >
              <span
                className="h-[11px] w-[11px]"
                style={pinned
                  ? { background: '#cbd5e1' }
                  : { boxShadow: 'inset 0 0 0 2px #41526e' }}
              />
            </button>
            <button
              type="button"
              onClick={() => { setPinned(false); setHover(false); setPanel('content') }}
              title="collapse panel"
              className="grid h-full place-items-center px-3 text-2xl font-black leading-none text-ink-100 hover:bg-white/10 hover:text-white"
              style={{ boxShadow: 'inset 5px 0 0 rgba(138,160,200,0.7)' }}
            >
              ›
            </button>
          </div>

          {/* body */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {panel === 'accounts' ? (
              <AccountsBody />
            ) : panel === 'settings' ? (
              <SettingsBody />
            ) : panel === 'activity' ? (
              <ActivityFeed />
            ) : panel === 'pinned' ? (
              <PinnedFeeds />
            ) : homeTab === 'activity' ? (
              <GalleryControlsBody />
            ) : homeTab === 'feed' ? (
              <FeedViewControlsBody />
            ) : homeTab === 'profile' ? (
              <ProfileGalleryControlsBody />
            ) : (
              <SettingsBody />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
